import type { BlockComponent, RegistryThemeBundle } from '@akademi/site-builder-runtime';
import { sorsyTheme } from '@commerce/theme-sorsy';
import { BRAND_NAME, DESIGNER_HREF, GUARANTEE_PLACEHOLDER, TEMPLATE_KEY, THEME_ID } from './brand';
import { SolarAccountDesignsLink } from './blocks/SolarAccountDesignsLink';
import { SolarAssurances } from './blocks/SolarAssurances';
import { SolarCategoryTiles } from './blocks/SolarCategoryTiles';
import { SolarDesignCta } from './blocks/SolarDesignCta';
import { SolarHero } from './blocks/SolarHero';
import { SolarHowItWorks } from './blocks/SolarHowItWorks';
import { SolarKitCards } from './blocks/SolarKitCards';
import { SolarOfferSlider } from './blocks/SolarOfferSlider';
import { SolarPromises } from './blocks/SolarPromises';
import { SolarProductRail } from './blocks/SolarProductRail';
import { SolarPromoGrid } from './blocks/SolarPromoGrid';
import { SolarTradeApplication } from './blocks/SolarTradeApplication';
import { SolarTradeBand } from './blocks/SolarTradeBand';
import { SolarTrustFigures } from './blocks/SolarTrustFigures';
import { tradeSkinOverrides } from './functional/tradeSkinOverrides';
import { SolarDepartmentBar } from './chrome/SolarDepartmentBar';
import { SolarHeaderSearch } from './chrome/SolarHeaderSearch';
import { SolarShopIntro } from './blocks/SolarShopIntro';
import { SolarProjects } from './blocks/SolarProjects';
import { RequestQuote } from './blocks/RequestQuote';

export { BRAND, BRAND_NAME, DELIVERY_PLACEHOLDER, DESIGNER_HREF, GUARANTEE_PLACEHOLDER, MY_DESIGNS_HREF, QUICK_QUOTE_HREF, TEMPLATE_KEY, THEME_ID } from './brand';
export { SolarAccountDesignsLink, type SolarAccountDesignsLinkProps } from './blocks/SolarAccountDesignsLink';
export { SolarCategoryTiles, type SolarCategoryTile, type SolarCategoryTilesProps } from './blocks/SolarCategoryTiles';
export { SolarDesignCta, type SolarDesignCtaProps } from './blocks/SolarDesignCta';
export { SolarHero, type SolarHeroFeature, type SolarHeroProps } from './blocks/SolarHero';
export { SolarHowItWorks, type SolarHowItWorksProps, type SolarHowItWorksStep } from './blocks/SolarHowItWorks';
export { SolarPromises, type SolarPromise, type SolarPromisesProps } from './blocks/SolarPromises';
export { SolarPromoGrid, type SolarPromoTile, type SolarPromoGridProps } from './blocks/SolarPromoGrid';
export { SolarTrustFigures, type SolarTrustFigure, type SolarTrustFiguresProps } from './blocks/SolarTrustFigures';
export { SolarOfferSlider, type SolarOfferSlide, type SolarOfferSliderProps } from './blocks/SolarOfferSlider';
export { SolarAssurances, type SolarAssurancesProps } from './blocks/SolarAssurances';
export { SolarKitCards, type SolarKit, type SolarKitCardsProps } from './blocks/SolarKitCards';
export { SolarTradeBand, type SolarTradeBandProps } from './blocks/SolarTradeBand';
export { SolarTradeApplication, type SolarTradeApplicationProps } from './blocks/SolarTradeApplication';
export { SolarProductRail, type SolarProductRailProps } from './blocks/SolarProductRail';
export { SolarShopIntro, type SolarShopIntroProps } from './blocks/SolarShopIntro';
export { SolarProjects, type SolarProjectsProps } from './blocks/SolarProjects';
export { RequestQuote } from './blocks/RequestQuote';
export { parseQuickOrder, matchesCode, type QuickOrderLine } from './home/quickOrder';
export { toMinor, totalOf, formatMinor, type LineTotal, type LiveMoney } from './home/money';
export { SCENE_ARTS, TILE_ICONS, type SceneArt, type TileIcon } from './home/sceneArt';
export { HOME_CSS } from './styles/homeCss';
export { locales, NS } from './locales';
export { SKINNED_BLOCKS, type SkinWidth } from './functional/skinnedBlocks';
export { tradeSkinOverrides } from './functional/tradeSkinOverrides';
export { TradeSkin, TradeSurface, type TradeSkinProps } from './functional/withTradeSkin';
export { DelegatingType } from './functional/delegation';
export { SolarDepartmentBar, departmentsFrom } from './chrome/SolarDepartmentBar';
export { SolarHeaderSearch, headerSearchFor } from './chrome/SolarHeaderSearch';
export { ChromeStyles, CHROME_CSS } from './chrome/ChromeStyles';
export type { SolarChromeProps } from './chrome/types';

/**
 * This vertical's header chrome, handed to the storefront through the extension's
 * `headerChrome` field and selected there by the site's own `enabledPlugins`.
 *
 * <p>`search` replaces the header's 220px modal-opening pill with a wide bar whose
 * suggestions drop down in place. `underHeader` fills the strip between header and main
 * with the departments the operator already authored under "Shop".
 *
 * <p>Both are CLIENT components. The storefront resolves this object on the server and
 * renders what it names without ever calling it, which is the same boundary
 * `catalogueSurface` crosses.
 */
export const solarHeaderChrome = {
  search: SolarHeaderSearch,
  underHeader: SolarDepartmentBar,
} as const;

const block = (component: unknown) => component as BlockComponent;

/** Solar additions only. The spread keeps every built-in sorsy block and chrome. */
export const solarBlocks: Record<string, BlockComponent> = {
  SolarCategoryTiles: block(SolarCategoryTiles),
  SolarDesignCta: block(SolarDesignCta),
  SolarHero: block(SolarHero),
  SolarHowItWorks: block(SolarHowItWorks),
  SolarPromises: block(SolarPromises),
  SolarPromoGrid: block(SolarPromoGrid),
  SolarAccountDesignsLink: block(SolarAccountDesignsLink),
  SolarTrustFigures: block(SolarTrustFigures),
  // The home page the owner chose on 27 September 2026 ("Home B"). The blocks above stay
  // registered because other pages, and the live home page until the template is next
  // applied, still name them.
  SolarOfferSlider: block(SolarOfferSlider),
  SolarAssurances: block(SolarAssurances),
  SolarKitCards: block(SolarKitCards),
  SolarTradeBand: block(SolarTradeBand),
  SolarProductRail: block(SolarProductRail),
  SolarShopIntro: block(SolarShopIntro),
  SolarProjects: block(SolarProjects),
  RequestQuote: block(RequestQuote),
  /**
   * NOT a `Solar…` type name, and that is the point: this entry REPLACES the platform's own
   * `TradeApplicationForm` wherever this theme is on.
   *
   * <p>Keeping the platform's type name is what makes the swap free. The live trade page
   * (2000001000001), `templates/solar-pv.json` and `scripts/build-shell-and-home.py` all
   * name `TradeApplicationForm`; a `SolarTradeApplication` type would need a seed schema
   * JSON, a template edit, a `push-seed --apply` and a write to a trading shop's page — four
   * changes to ship a layout. `createBlockResolver` reads `themes[themeId].blocks[type]`
   * before `defaultBlocks[type]`, so the theme simply wins, and a shop not running this
   * vertical still gets the shared block.
   *
   * <p>It must therefore keep the shared block's PROP CONTRACT: `title`, `intro`,
   * `signInHref`, `accountHref`, `termsHref` are what the live page already passes.
   *
   * <p>The spread order below (skins first, solar last) is what lets it win over the
   * functional skin — and `TradeApplicationForm` has been taken OUT of `SKINNED_BLOCKS`
   * besides, because a skin wrapping this block would re-enter the resolver and find this
   * block again. This block draws its own `.ae` scope and stylesheets instead.
   */
  TradeApplicationForm: block(SolarTradeApplication),
};

export const solarTheme: RegistryThemeBundle = {
  ...sorsyTheme,
  blocks: {
    ...(sorsyTheme.blocks ?? {}),
    // The functional skins go on BEFORE the solar blocks: an override here is
    // keyed by the platform block's own type, and nothing in `solarBlocks`
    // shares a name with one, so the order is about intent rather than
    // collision — a solar block must always win over a generic skin.
    ...tradeSkinOverrides,
    // NO SiteShell OVERRIDE. See the note at the bottom of this file.
    ...solarBlocks,
  },
  primitives: {
    ...(sorsyTheme.primitives ?? {}),
  },
};

/** Public metadata used by seed tooling without repeating brand copy in blocks. */
export const themeMetadata = { id: THEME_ID, brand: BRAND_NAME, designerHref: DESIGNER_HREF, guarantee: GUARANTEE_PLACEHOLDER } as const;

export default solarTheme;

/**
 * WHY THIS THEME DOES NOT OVERRIDE `SiteShell` — 25 September 2026
 *
 * <p>It did, for about an hour, to build a Toolstation-style department bar: a solid red
 * strip of category links under the lockup. The shell cannot be configured into one
 * (`header.style` offers only `classic`, `sidebar`, `sidebar-collapsible`, `none`), so the
 * override injected CSS that wrapped the nav onto its own row with `flex: 0 0 100%` and
 * bled it to the viewport edges with negative side margins.
 *
 * <p>It broke the live header twice, and the second time badly enough that the owner saw
 * it on a trading shop. The reason it kept failing is worth keeping: the header's layout
 * lives in INLINE styles on elements this theme does not own, so every rule is a
 * specificity fight whose outcome cannot be read from the markup — and it is not
 * renderable here, because this workstation runs no browser. Each attempt was therefore
 * reasoned, shipped, and judged by eye on production. That is not a way to build a header.
 *
 * <p>What it would take to do properly: a `headerBottom`/nav-bar slot on `SiteShellPresenter`
 * in the akademi base, authored like `topBar` already is, so the bar is markup the shell
 * renders rather than CSS fighting markup it does not expect. That is a change to the
 * pinned base, and belongs in a task with a way to see the result.
 *
 * <h3>Resolved, later the same day — and the base change was not needed</h3>
 *
 * <p><strong>That slot already existed.</strong> `SiteShellPresenter` takes
 * `headerCategorySlot`, which renders its content in a full-width strip between the header
 * and `main` and whose own comment says the slot content owns its internal layout; it takes
 * `headerSearchSlot` for the search affordance beside it. Both are documented props. The
 * bar is now {@link SolarDepartmentBar} and the search {@link SolarHeaderSearch}, filling
 * those two slots as ordinary markup with classes nothing else uses — no nav rewrapping, no
 * negative margins, and one `:has()` rule reaching the strip wrapper by what it contains.
 *
 * <p>It is still NOT a `SiteShell` override, for a second reason the original note did not
 * reach: one storefront process serves every shop through one `SiteShell`, so chrome put
 * there travels to all of them. It hangs off this vertical's EXTENSION instead
 * ({@link solarHeaderChrome} → `headerChrome`), selected by the site's own `enabledPlugins`
 * exactly as `catalogueSurface` is, so a kitchen shop cannot inherit it however its shell is
 * configured. The stylesheet moved with it: `ChromeStyles` ships through this theme's own
 * hoisted `<style>`, so a shop not running this vertical carries none of these rules.
 */
